import { AnyObj, handleUpload, removeUndefinedFields } from "../utils"
import {
  DocumentData,
  addDoc,
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore"
import React, { createContext, useContext, useEffect, useState } from "react"
import { auth, db, storage } from "../firebase"
import { getBlob, getDownloadURL, ref, uploadBytes } from "firebase/storage"

import { useAuthState } from "react-firebase-hooks/auth"
import { LoadingService } from "react-ui-components/components/LoadingService"
import { DEFAULT_AVAILABLE_LOCATION } from "react-ui-components/components/location/LocationSelector"
import { ILocation } from "react-ui-components/components/models/ILocation"
import { IRaffle } from "react-ui-components/components/models/IRaffle"
import { GOAL_RAFFLE_TYPE } from "react-ui-components/components/models/IRaffleGoal"
import { prepareRaffleForSave } from "./raffle/raffleFormUtils"
import { assertRaffleMediaItemsSafeForPersistence } from "./raffle/raffleMediaPersistenceGuards"
import {
  DEFAULT_DISPLAY_CONTENT_PUBLICATIONS_CAROUSEL_CONFIG,
  DEFAULT_DISPLAY_CONTENT_VIDEO_PLAYBACK_CONFIG,
  mergeDisplayContentConfigDefaults,
} from "./Configuration/displayContentConfigDefaults"

export const DEFAULT_PREPAY_TICKET_CODE_PREFIX = "https://winfacil.com/?{{lang}}&{{country}}&{{state}}&{{city}}&{{other}}&code="

/**
 * Service object providing methods for managing application configuration
 * Handles fetching and updating app-wide settings stored in Firebase
 */
export const AppConfigurationService = {
  /**
   * Fetches the main display content configuration from Firebase
   * @param onSuccess - Callback function called with the configuration data
   */
  getConfig: async (onSuccess: any) => {
    try {
      const docData = await getDoc(doc(db, "app-configurations", "display-content-config"))
      const data = docData.data()
      onSuccess && onSuccess(data)
    } catch (error: any) {
      console.error("Error getting app configuration:", error)
      throw new Error(`Failed to get app configuration: ${error.message}`)
    }
  },

  /**
   * Fetches available locations configuration from Firebase
   * @param onSuccess - Callback function called with the locations data
   */
  fetchLocations: async (onSuccess: any) => {
    LoadingService.show()
    try {
      const docRef = doc(db, "app-configurations/availableLocations")
      const docSnap = await getDoc(docRef)
      if (docSnap.exists()) {
        const data = docSnap.data()
        onSuccess && onSuccess(data)
      }
    } catch (error) {
      console.error("Error fetching locations:", error)
    } finally {
      LoadingService.hide()
    }
  },
}

/**
 * Default configuration data used when no Firebase data is available
 * Provides fallback values for the application configuration
 */
const DEFAULT_DATA: {
  availableLocations: ILocation
  configuration: {
    prepayTicketCodePrefixDefault: string
    raffleTypes: string[]
    rafflesScreen: {
      descriptionLength: number
      hideAllActions: boolean
      imageResizeMode: string
      rafflesImageHeight: number
      rafflesSectionImageHeight: number
    }
    publicationsCarousel: {
      autoPlay: boolean
      intervalSeconds: number
    }
    videoPlayback: {
      awayBehavior: "floating" | "stopOnAway"
      floatingPopupEnabled: boolean
      heartbeatSeconds: number
      raffleVideo: {
        autoplay: boolean
        controls: boolean
        fullscreenEnabled: boolean
        frameBackgroundEnabled: boolean
        frameBorderEnabled: boolean
        frameBorderRadius: number
        loop: boolean
        muted: boolean
        objectFit: "contain" | "cover" | "fill" | "none" | "scale-down"
        showWinViewCount: boolean
        winViewCountPlacement: "outsideBelow" | "insideBelow"
      }
      viewThresholdSeconds: number
    }
  }
  saveConfig: (data: any) => Promise<void>
  saveRaffle: (data: any) => Promise<any>
  disableRaffle: (id: string, disabled?: boolean) => Promise<any>
  duplicateRaffle: (data: any) => Promise<any>
} = {
  availableLocations: { US: { FL: ["Orlando"] } },
  configuration: {
    prepayTicketCodePrefixDefault: DEFAULT_PREPAY_TICKET_CODE_PREFIX,
    raffleTypes: ["Raffle", "Event", GOAL_RAFFLE_TYPE],
    rafflesScreen: {
      descriptionLength: 50,
      hideAllActions: true,
      imageResizeMode: "contain",
      rafflesImageHeight: 500,
      rafflesSectionImageHeight: 500,
    },
    publicationsCarousel: DEFAULT_DISPLAY_CONTENT_PUBLICATIONS_CAROUSEL_CONFIG,
    videoPlayback: DEFAULT_DISPLAY_CONTENT_VIDEO_PLAYBACK_CONFIG,
  },
  saveConfig: async (_data: any) => {
    console.error("saveConfig not implemented", _data)
  },
  saveRaffle: async (_data: any) => {
    console.error("saveRaffle not implemented", _data)
  },
  disableRaffle: async (id: string, disabled = true) => {
    console.error("disableRaffle not implemented", { id, disabled })
  },
  duplicateRaffle: async (_data: any) => {
    console.error("duplicateRaffle not implemented", _data)
  },
}

const ensureGoalRaffleType = (configurationData: any) => {
  if (!configurationData || typeof configurationData !== "object") {
    return configurationData
  }

  const raffleTypes = Array.isArray(configurationData.raffleTypes)
    ? configurationData.raffleTypes
    : DEFAULT_DATA.configuration.raffleTypes

  return {
    ...configurationData,
    raffleTypes: raffleTypes.includes(GOAL_RAFFLE_TYPE)
      ? raffleTypes
      : [...raffleTypes, GOAL_RAFFLE_TYPE],
  }
}

const uploadGoalActionIcon = async (raffleData: any, id: string, prevValue?: string) => {
  const actionIconAsset = raffleData?.goalSettings?.actionIconAsset
  if (!actionIconAsset) {
    return raffleData
  }

  const uploadedActionIconAsset = await handleUpload({
    image: actionIconAsset,
    id,
    fieldName: "goalSettings",
    imagesBucket: "raffles",
    prevValue,
  })

  return {
    ...raffleData,
    goalSettings: {
      ...raffleData.goalSettings,
      actionIconAsset: uploadedActionIconAsset,
    },
  }
}

/**
 * React context for sharing application configuration data across components
 */
const AppConfigurationContext = createContext(DEFAULT_DATA)

/**
 * Hook to access the application configuration context
 * @returns Current configuration data and methods
 */
export const useAppConfiguration = () => {
  return useContext(AppConfigurationContext)
}

/**
 * Props interface for the AppConfigurationProvider component
 */
interface AppConfigurationProviderProps {
  children: any
  defaultAppConfiguration?: typeof DEFAULT_DATA | null
}

/**
 * Provider component that manages application configuration state
 * Fetches configuration from Firebase and provides context to child components
 * @param props - Component props
 * @returns Context provider wrapping children
 */
export const AppConfigurationProvider = (props: AppConfigurationProviderProps) => {
  const { children, defaultAppConfiguration = DEFAULT_DATA } = props
  const [configuration, setConfiguration] = useState<any>(defaultAppConfiguration)
  const [availableLocations, setAvailableLocations] = useState<ILocation>()

  const [user] = useAuthState(auth)

  /**
   * Saves application configuration data to Firebase
   * @param data - Configuration data to save
   */
  async function saveConfig(data: AnyObj[] | AnyObj | null) {
    try {
      data = removeUndefinedFields(
        ensureGoalRaffleType(mergeDisplayContentConfigDefaults(data, DEFAULT_DATA.configuration)),
      )
      const snapshot = doc(db, "app-configurations", "display-content-config")
      const id = snapshot.id
      await updateDoc(snapshot, { id, ...data })
    } catch (error: any) {
      console.error("Error saving app configuration:", error)
      throw new Error(`Failed to save app configuration: ${error.message}`)
    }
  }

  /**
   * Disables or enables a raffle by updating its disabled status
   * @param raffleId - ID of the raffle to update
   * @param disabled - Whether to disable the raffle (default: true)
   * @returns Success message or error
   */
  async function disableRaffle(raffleId: string, disabled = true) {
    try {
      if (!raffleId) {
        return "No data to save"
      }
      const id = raffleId

      await updateDoc(doc(db, "raffles", id), { id, disabled })

      return "Success"
    } catch (error: any) {
      console.error("Error disabling raffle:", error)
      throw new Error(`Failed to disable raffle: ${error.message}`)
    }
  }

  /**
   * Duplicates a raffle and all its associated assets
   * @param raffleData - Original raffle data to duplicate
   * @returns New raffle data with duplicated assets
   */
  async function duplicateRaffle(raffleData: any) {
    try {
      if (!raffleData) {
        return "No data to save"
      }

      const newDocRef = doc(collection(db, "raffles"))

      // Get the document's auto-generated ID
      const id = newDocRef.id
      const preparedData = await uploadGoalActionIcon(
        prepareRaffleForSave(raffleData),
        id,
      )
      assertRaffleMediaItemsSafeForPersistence(preparedData?.raffleMediaItems)
      const data: IRaffle = removeUndefinedFields(preparedData) as any

      const newData = await duplicateRaffleAndAssets(data, id)

      await setDoc(newDocRef, newData)

      return newData
    } catch (error: any) {
      console.error("Error duplicating raffle:", error)
      throw new Error(`Failed to duplicate raffle: ${error.message}`)
    }
  }

  /**
   * Saves raffle data to Firebase
   * @param raffleData - Raffle data to save
   * @returns Saved raffle data
   */
  async function saveRaffle(raffleData: any) {
    try {
      if (!raffleData) {
        return "No data to save"
      }

      const id = raffleData.id
      const preparedData = await uploadGoalActionIcon(
        prepareRaffleForSave(raffleData),
        id,
      )
      assertRaffleMediaItemsSafeForPersistence(preparedData?.raffleMediaItems)
      const data: any = removeUndefinedFields(preparedData)
      const savedRaffle = { id, ...data }

      await updateDoc(doc(db, "raffles", id), savedRaffle)

      return savedRaffle
    } catch (error: any) {
      console.error("Error saving raffle:", error)
      throw new Error(`Failed to save raffle: ${error.message}`)
    }
  }

  useEffect(() => {
    if (user) {
      AppConfigurationService.getConfig((data: any) => {
        setConfiguration(
          ensureGoalRaffleType(
            mergeDisplayContentConfigDefaults(data, DEFAULT_DATA.configuration),
          ),
        )
      })
      AppConfigurationService.fetchLocations(setAvailableLocations)
    }
  }, [user])

  return (
    <AppConfigurationContext.Provider
      value={{
        configuration,
        saveConfig,
        availableLocations: availableLocations || DEFAULT_AVAILABLE_LOCATION,
        saveRaffle,
        disableRaffle,
        duplicateRaffle,
      }}
    >
      {children}
    </AppConfigurationContext.Provider>
  )
}

export async function duplicateRaffleAndAssets(raffleData: IRaffle, id: string): Promise<IRaffle> {
  if (!raffleData) {
    throw new Error(`Invalid Original Document`)
  }

  const imagesURLs: string[] = []
  for (let index = 0; index < raffleData?.raffleImages?.length || 0; index++) {
    const originalAssetPath = raffleData.raffleImages[index]

    // Retrieve the download URL of the original asset
    // let assetURL: string
    let assetBlob: Blob | null = null
    try {
      // If originalAssetPath is already a URL, you can skip getDownloadURL. Otherwise:
      const originalAssetRef = ref(storage, originalAssetPath)
      assetBlob = await getBlob(originalAssetRef)
      // assetURL = /^https/i.test(originalAssetPath)
      //   ? originalAssetPath
      //   : await getDownloadURL(originalAssetRef)
    } catch (error) {
      console.log(`Failed to get the download URL for the asset: ${(error as Error).message}`)
      continue
    }

    // Fetch the asset as a Blob or ArrayBuffer
    try {
      const img = (await handleUpload({
        blob: assetBlob,
        image: originalAssetPath,
        id,
        fieldName: "assets",
        imagesBucket: "raffles",
        prevValue: "",
      })) as string
      imagesURLs.push(img)
    } catch (error) {
      console.log("Error getting image from raffle", error)
      continue
    }
  }
  if (raffleData.id) {
    await duplicateRaffleAssets(raffleData.id, id)
  }
  // Create a new doc with the same data, except with the updated asset field
  return {
    ...raffleData,
    name: `${raffleData.name} (duplicate)`,
    id,
    disabled: true,
    raffleImages: imagesURLs,
  }
}

async function duplicateRaffleAssets(sourceRaffleId: string, targetRaffleId: string) {
  try {
    const assetsSnapshot = await getDocs(collection(db, "raffles", sourceRaffleId, "assets"))
    if (assetsSnapshot.empty) {
      return
    }

    const now = new Date().toISOString()
    const assetWrites = assetsSnapshot.docs.map(async (assetDoc) => {
      const newAssetRef = doc(collection(db, "raffles", targetRaffleId, "assets"))
      const rawData = assetDoc.data()
      const cleanData = removeUndefinedFields({ ...rawData }) || {}

      await setDoc(newAssetRef, {
        ...cleanData,
        id: newAssetRef.id,
        createdAt: now,
        updatedAt: now,
      })
    })

    await Promise.all(assetWrites)
  } catch (error: any) {
    console.error("Error duplicating raffle assets:", error)
    throw new Error(`Failed to duplicate raffle assets: ${error.message}`)
  }
}

/**
 * Duplicates a Firestore document along with its associated asset from Firebase Storage.
 *
 * @param originalCollection - The Firestore collection where the original document resides.
 * @param originalDocId - The ID of the original document to duplicate.
 * @param assetField - The field name in the document that stores the asset's storage path or URL.
 * @param duplicateCollection - The Firestore collection where the duplicated document should be placed.
 * @param newAssetStoragePath - The new path in Firebase Storage where the duplicated asset will be stored.
 *
 * @returns The newly created document’s ID.
 */
export async function duplicateDocumentAndAsset(
  originalCollection: string,
  originalDocId: string,
  assetField: string,
  duplicateCollection?: string,
  newAssetStoragePath?: string,
): Promise<string> {
  // Use the same collection by default if none provided
  const targetCollection = duplicateCollection || originalCollection

  // Reference to the original document
  const originalDocRef = doc(db, originalCollection, originalDocId)
  const originalSnapshot = await getDoc(originalDocRef)

  if (!originalSnapshot.exists()) {
    throw new Error(
      `Document with ID ${originalDocId} does not exist in collection ${originalCollection}`,
    )
  }

  // Original document data
  const originalData = originalSnapshot.data() as DocumentData

  // Check if asset field is present
  if (!originalData[assetField]) {
    throw new Error(
      `The specified asset field "${assetField}" does not exist in the original document.`,
    )
  }

  const originalAssetPath = originalData[assetField]

  // Retrieve the download URL of the original asset
  let assetURL: string
  try {
    // If originalAssetPath is already a URL, you can skip getDownloadURL. Otherwise:
    const originalAssetRef = ref(storage, originalAssetPath)
    assetURL = await getDownloadURL(originalAssetRef)
  } catch (error) {
    throw new Error(`Failed to get the download URL for the asset: ${(error as Error).message}`)
  }

  // Fetch the asset as a Blob or ArrayBuffer
  const response = await fetch(assetURL)
  const assetBlob = await response.blob()

  // Prepare the new asset path. If not provided, just append something random or a timestamp.
  const duplicatedAssetPath = newAssetStoragePath || `${originalAssetPath}-duplicate-${Date.now()}`

  // Upload the asset to the new storage location
  const duplicatedAssetRef = ref(storage, duplicatedAssetPath)
  await uploadBytes(duplicatedAssetRef, assetBlob)

  // Get the new asset download URL
  const duplicatedAssetURL = await getDownloadURL(duplicatedAssetRef)

  // Create a new doc with the same data, except with the updated asset field
  const newData = {
    ...originalData,
    [assetField]: duplicatedAssetURL,
  }

  // Add the new document to the target collection
  // Using addDoc will auto-generate an ID
  const targetCollectionRef = collection(db, targetCollection)
  const newDocRef = await addDoc(targetCollectionRef, newData)

  return newDocRef.id
}
